// Outbox processor: mirrors locally-committed writes to the server (safety copy).
// Replays each queued request verbatim with a stable Idempotency-Key so a retry after a
// lost response / app kill / reconnect NEVER creates a duplicate server record.
import { listPending, markSynced, markFailed, upsertRecord, removeRecord, patchCaches } from "./repo";

function isNetworkError(e) {
  return !e || !e.response || e.code === "ERR_NETWORK" || e.message === "Network Error";
}

// For a create, the server response shape varies (e.g. POST /practices -> {practice,...}).
// Generic record entities return the record directly.
function extractServerRecord(entity, data) {
  if (!data) return null;
  if (entity === "practices" && data.practice) return data.practice;
  if (data.id) return data;
  return null;
}

async function reconcile(exec, op, serverData) {
  const { meta } = op;
  const table = meta.table;
  if (op.method === "DELETE") return; // already removed locally
  if (meta.isCreate) {
    // Swap the temp (client-id) record for the authoritative server record.
    if (meta.entityId) {
      await removeRecord(exec, table, meta.entityId);
      await patchCaches(exec, meta.pathPrefix, { op: "remove", id: meta.entityId });
    }
    const srv = extractServerRecord(meta.entity, serverData);
    if (srv && srv.id) {
      await upsertRecord(exec, table, srv, { pending: 0 });
      await patchCaches(exec, meta.pathPrefix, { op: "upsert", item: srv });
    }
  } else if (serverData && serverData.id) {
    await upsertRecord(exec, table, serverData, { pending: 0 });
    await patchCaches(exec, meta.pathPrefix, { op: "upsert", item: serverData });
  }
}

// Send a single queued op. Returns the axios response on success; throws on network error
// so the caller can stop and keep the queue for a later retry.
export async function processOp(exec, netApi, op) {
  const res = await netApi.request({
    method: op.method,
    url: op.url,
    data: op.method === "DELETE" ? undefined : op.body,
    headers: { "Idempotency-Key": op.key },
  });
  await reconcile(exec, op, res.data);
  await markSynced(exec, op.key);
  return res;
}

let _running = false;

export async function processOutbox(exec, netApi) {
  if (_running) return { skipped: "already-running" };
  if (typeof navigator !== "undefined" && navigator.onLine === false) return { skipped: "offline" };
  _running = true;
  const result = { synced: 0, failed: 0, stopped: false };
  try {
    const pending = await listPending(exec);
    for (const op of pending) {
      try {
        await processOp(exec, netApi, op);
        result.synced += 1;
      } catch (e) {
        if (isNetworkError(e)) { result.stopped = true; break; } // still offline — retry later
        // Server rejected (4xx/409). Mark as failed (poison) but keep the local record.
        await markFailed(exec, op.key, e?.response?.data?.detail || e?.message);
        result.failed += 1;
      }
    }
  } finally {
    _running = false;
  }
  return result;
}

export { isNetworkError };
